import { useState } from "react";
import { Layout, Menu, Select } from "antd";
import {
  ArrowLeftOutlined,
  DoubleLeftOutlined,
  DoubleRightOutlined,
  FileTextOutlined,
  LineChartOutlined,
  ApartmentOutlined,
  DollarOutlined,
} from "@ant-design/icons";
import {
  Link,
  Outlet,
  useLocation,
  useNavigate,
  useParams,
} from "react-router";

import { useUser } from "@/contexts/UserContext";
import { useFeatureFlag } from "@/hooks/useFeatureFlag";
import { FEATURE_FLAGS } from "@/utils/feature-flag-constants";
import {
  PLANNING_TABS,
  SIMPLIFIED_PLANNING_TABS,
  SIMPLIFIED_ENTRY_STEP,
} from "./steps";
import { useTrialPlans } from "@/hooks/useTrialPlans";
import LogoIcon from "@/assets/icons/latest_vivo_logo.svg?react";
import VivoLogo from "@/assets/icons/vivo_logo.svg?react";
import SitesIcon from "@/assets/icons/sites.svg?react";
import PortfolioMilestonesIcon from "@/assets/icons/portfolio_milestones.svg?react";
import ChecklistIcon from "@/assets/icons/checklist.svg?react";
import CardsStackIcon from "@/assets/icons/cards_stack.svg?react";
import CalendarIcon from "@/assets/icons/calendar_month.svg?react";
import ButtonMenu from "@/components/UI/ButtonMenu";
import UserMenu from "@/components/UI/UserMenu";
import AskVivoDrawer from "@/components/Chat/AskVivoDrawer";

const { Sider, Content } = Layout;

/** Items below Scenarios are content of the open scenario; a level of
 *  indentation (not a submenu) communicates that containment. */
const SCENARIO_SCOPED_STYLE = { paddingLeft: 26 };

export default function PlanningShell() {
  const [collapsed, setCollapsed] = useState(false);
  const { id, planId } = useParams<{ id?: string; planId?: string }>();
  const { userInfo } = useUser();
  // A simplified plan is one linear pass: pick sites, refine them, project
  // enrollment. Everything else the shell offers is a separate planning
  // discipline the tenant does not run here.
  const simplified = useFeatureFlag(FEATURE_FLAGS.PLANNING_SIMPLIFIED, true);
  const { plans } = useTrialPlans();
  const location = useLocation();
  const navigate = useNavigate();

  const tabs = simplified ? SIMPLIFIED_PLANNING_TABS : PLANNING_TABS;
  const activeKey = location.pathname.split("/").at(-1) ?? "";
  const currentTab = (tabs as readonly string[]).includes(activeKey)
    ? activeKey
    : simplified
      ? SIMPLIFIED_ENTRY_STEP
      : "sites";

  const planOptions = plans.map((p) => ({ value: p.id, label: p.name }));

  const handlePlanSelect = (newPlanId: string) => {
    navigate(`/planning/${id}/${newPlanId}/${currentTab}`);
  };

  const allNavItems = planId
    ? [
        {
          key: "home",
          icon: <FileTextOutlined />,
          label: <Link to={`/planning/${id}/${planId}/home`}>Overview</Link>,
        },
        {
          key: "scenarios",
          icon: <CardsStackIcon className="w-[18px] h-[18px]" />,
          label: (
            <Link to={`/planning/${id}/${planId}/scenarios`}>Scenarios</Link>
          ),
        },
        { type: "divider" as const, key: "div-1" },
        {
          key: "shortlist",
          style: SCENARIO_SCOPED_STYLE,
          icon: <ChecklistIcon className="w-[18px] h-[18px]" />,
          label: (
            <Link to={`/planning/${id}/${planId}/shortlist`}>
              Find Best Sites
            </Link>
          ),
        },
        {
          key: "sites",
          style: SCENARIO_SCOPED_STYLE,
          icon: <SitesIcon className="w-[18px] h-[18px]" />,
          label: (
            <Link to={`/planning/${id}/${planId}/sites`}>Refine Site List</Link>
          ),
        },
        {
          key: "siteplan",
          style: SCENARIO_SCOPED_STYLE,
          icon: <CalendarIcon className="w-[18px] h-[18px]" />,
          label: (
            <Link to={`/planning/${id}/${planId}/siteplan`}>
              Site Milestones
            </Link>
          ),
        },
        {
          key: "milestones",
          style: SCENARIO_SCOPED_STYLE,
          icon: <PortfolioMilestonesIcon className="w-[18px] h-[18px]" />,
          label: (
            <Link to={`/planning/${id}/${planId}/milestones`}>
              Trial Milestones
            </Link>
          ),
        },
        {
          key: "enrollment",
          style: SCENARIO_SCOPED_STYLE,
          icon: <LineChartOutlined />,
          label: (
            <Link to={`/planning/${id}/${planId}/enrollment`}>Enrollment</Link>
          ),
        },
        { type: "divider" as const, key: "div-2" },
        {
          key: "vendors",
          style: SCENARIO_SCOPED_STYLE,
          icon: <ApartmentOutlined />,
          label: (
            <Link to={`/planning/${id}/${planId}/vendors`}>
              CRO &amp; Vendors
            </Link>
          ),
        },
        {
          key: "budget",
          style: SCENARIO_SCOPED_STYLE,
          icon: <DollarOutlined />,
          label: <Link to={`/planning/${id}/${planId}/budget`}>Budget</Link>,
        },
      ]
    : [];

  // Under simplification the three remaining steps are the whole flow rather
  // than content nested under a scenario, so the indentation and the dividers
  // that expressed that containment come off with them.
  const navItems = simplified
    ? allNavItems
        .filter(
          (item) =>
            "key" in item &&
            (SIMPLIFIED_PLANNING_TABS as readonly string[]).includes(item.key),
        )
        .map((item) => ({ ...item, style: undefined }))
    : allNavItems;

  const firstInitial = userInfo?.first_name?.charAt(0) ?? "?";

  return (
    <Layout style={{ minHeight: "100vh" }}>
      <Sider
        width={256}
        collapsible
        trigger={null}
        collapsed={collapsed}
        onCollapse={setCollapsed}
        style={{ height: "100vh", position: "fixed" }}
        collapsedWidth={80}
      >
        <div
          className={`flex flex-col h-full py-3 overflow-y-auto ${collapsed ? "px-5" : "px-6"}`}
        >
          {/* Logo */}
          <div>
            {collapsed ? (
              <div className="flex flex-col items-center mt-[24px] mb-3">
                <Link to={`/portfolio/trials`}>
                  <VivoLogo className="w-[32px] h-[18px]" />
                </Link>
              </div>
            ) : (
              <div className="flex flex-col mt-[24px] mb-3">
                <Link to={`/portfolio/trials`}>
                  <LogoIcon />
                </Link>
              </div>
            )}
          </div>

          {/* Plan selector */}
          {!collapsed && (
            <div className="mt-3">
              <Select
                placeholder={
                  simplified ? "Select a scenario…" : "Select a plan…"
                }
                className="w-full"
                options={planOptions}
                value={planId ?? undefined}
                onSelect={handlePlanSelect}
                labelRender={({ value }) =>
                  plans.find((p) => p.id === value)?.name ?? String(value)
                }
                notFoundContent={
                  <span className="text-[12px] text-[#6B7280]">
                    {simplified ? "No scenarios yet" : "No plans yet"}
                  </span>
                }
              />
            </div>
          )}

          {/* Nav menu */}
          <div
            className={`flex-1 overflow-y-auto hide-scrollbar text-white ${collapsed ? "mt-2" : "mt-4"}`}
          >
            {navItems.length > 0 && (
              <Menu
                theme="dark"
                mode="inline"
                inlineIndent={3}
                className="trial-main-nav"
                classNames={{ popup: { root: "trial-main-nav-popup" } }}
                tooltip={{ classNames: { root: "trial-main-nav-tooltip" } }}
                items={navItems}
                selectedKeys={[activeKey]}
              />
            )}

            {/* Back link */}
            <div className="flex flex-col mt-3">
              <Link to={`/portfolio/planning`}>
                {!collapsed && (
                  <div className="flex gap-3 px-1 py-2 text-sm text-white/60 hover:text-white border-t border-white/10">
                    <ArrowLeftOutlined className="text-base" />
                    <p className="m-0">
                      {simplified ? "All Scenarios" : "All Trial Plans"}
                    </p>
                  </div>
                )}
              </Link>
            </div>
          </div>

          {/* User menu + collapse */}
          <div>
            {collapsed ? (
              <div className="flex flex-col items-center mb-4">
                <UserMenu mode="navigation">
                  <div className="bg-[#0E98D0] rounded-full h-7.5 w-7.5 flex justify-center items-center text-md border-none">
                    <span className="text-white">{firstInitial}</span>
                  </div>
                </UserMenu>
                <button
                  type="button"
                  onClick={() => setCollapsed(false)}
                  aria-label="Expand sidebar"
                  className="mt-5 h-10 w-10 shrink-0 flex items-center justify-center bg-[#ffffff1a] rounded-md text-white hover:bg-[#ffffff26] active:bg-[#ffffff33] transition-all duration-200"
                >
                  <DoubleRightOutlined />
                </button>
              </div>
            ) : (
              <div className="flex items-center gap-2 mt-5 mb-4">
                <div className="flex-1">
                  <UserMenu mode="navigation">
                    <ButtonMenu userInfo={userInfo} navbar={true} />
                  </UserMenu>
                </div>
                <button
                  type="button"
                  onClick={() => setCollapsed(true)}
                  aria-label="Collapse sidebar"
                  className="h-10 w-10 shrink-0 flex items-center justify-center bg-[#ffffff1a] rounded-[6px] text-white hover:bg-[#ffffff26] active:bg-[#ffffff33] transition-all duration-200"
                >
                  <DoubleLeftOutlined />
                </button>
              </div>
            )}
          </div>
        </div>
      </Sider>

      <Layout
        className={`bg-[#EFF1F2] relative max-h-fit ${collapsed ? "ml-[80px]" : "ml-[256px]"}`}
      >
        <Content style={{ overflow: "initial", background: "#EFF1F2" }}>
          <Outlet context={{ sidebarCollapsed: collapsed }} />
        </Content>
      </Layout>
      <AskVivoDrawer />
    </Layout>
  );
}
